import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronDown, Lightbulb, Radar } from "lucide-react";
import { api } from "@/lib/api";
import { rErr } from "@/lib/radar";
import { useRadar } from "@/components/radar/Bits";
import { GUIDES } from "@/lib/guides";
import { RunRadar } from "@/components/rcc/RunRadar";

// Opportunity Radar guides: open the Run radar dialog on not-run companies (or go to Imports when there are none).
const RadarNowBtn = () => {
  const { openRun } = useRadar();
  const nav = useNavigate();
  const go = async () => {
    try {
      const r = (await api.get("/admin/rcc/radar/companies?status=not_run&limit=100")).data;
      const ids = (r.items || []).map((c) => c.id);
      if (!ids.length) { toast.message("No companies waiting for a run. Import a CSV first."); return nav("/admin/rcc/radar/imports"); }
      openRun({ company_ids: ids, label: `${ids.length} not-run companies` });
    } catch (e) { toast.error(rErr(e)); }
  };
  return <button onClick={go} className="flex h-8 items-center gap-1.5 rounded-md bg-[#D4AF37] px-3 text-xs font-semibold text-black hover:bg-[#F3C649]" data-testid="run-radar-new"><Radar className="h-3.5 w-3.5" />Run radar now</button>;
};

// Short "how this works" guide; open until dismissed (remembered per page).
export const HowItWorks = ({ id }) => {
  const g = GUIDES[id];
  const key = `hiw_${id}`;
  const [open, setOpen] = useState(() => localStorage.getItem(key) !== "hidden");
  if (!g) return null;
  const toggle = () => { localStorage.setItem(key, open ? "hidden" : "open"); setOpen(!open); };
  return (
    <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/[0.07] to-transparent" data-testid={`hiw-${id}`}>
      <button onClick={toggle} className="flex w-full items-center gap-2 px-4 py-3 text-left" data-testid={`hiw-${id}-toggle`}>
        <Lightbulb className="h-4 w-4 shrink-0 text-[#D4AF37]" />
        <span className="flex-1 text-sm font-semibold text-[#F3C649]">{g.title || "How this works"}</span>
        <span className="text-[11px] text-zinc-500">{open ? "Got it, hide" : "Show"}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-3 px-4 pb-4">
        {g.what && <p className="text-sm text-zinc-300" data-testid={`hiw-${id}-what`}>{g.what}</p>}
        <ol className="space-y-1.5">{g.steps.map((s, i) => <li key={i} className="flex gap-2.5 text-sm text-zinc-300"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[11px] font-bold text-black">{i + 1}</span><span>{s}</span></li>)}</ol>
        {g.note && <p className="text-[11px] text-zinc-500">{g.note}</p>}
        {(g.radar || g.links?.length) && <div className="flex flex-wrap items-center gap-2 pt-1">
          {g.radar === "new" ? <RadarNowBtn /> : g.radar && <RunRadar />}
          {(g.links || []).map(([l, to]) => <Link key={to + l} to={to} className="flex items-center gap-1 rounded-full border border-[#8224e3]/50 px-3 py-1.5 text-xs text-[#d7b6ff] hover:bg-[#8224e3]/10" data-testid={`hiw-${id}-link-${to.split("/").pop()}`}>{l}<ArrowRight className="h-3 w-3" /></Link>)}
        </div>}
      </div>}
    </div>
  );
};
